/**
 * ========================================
 * PROTECTED ROUTE
 * ========================================
 * Ensures only authenticated users can access certain routes
 * Also checks if user's trial/subscription is still active
 * Redirects unauthenticated or expired users to login page
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { useEntitlements } from '@context/EntitlementContext';
import { routeFeature } from '@/config/featureRegistry';
import { isParentUser } from '@services/api/parent.service';
import { MainLayout } from '@components/layouts/MainLayout';
import { LockedFeature } from '@components/ui/LockedFeature';
import { OnboardingGuard } from './OnboardingGuard';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Plan feature this page needs. Defaults to whatever featureRegistry maps the current path to. */
  feature?: string;
}

/**
 * ProtectedRoute Component
 * Wraps routes that require authentication and valid subscription
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, feature }) => {
  const { isAuthenticated, isLoading, canAccess, user } = useAuth();
  const location = useLocation();
  const { has } = useEntitlements();
  const requiredFeature = feature ?? routeFeature(location.pathname);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        fontSize: '16px',
        color: '#666',
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #f0f0f0',
            borderRadius: '50%',
            borderTopColor: '#667eea',
            animation: 'spin 0.6s linear infinite',
          }}></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  // (/join-school/:token is a public route now -- JoinSchool.tsx itself sets postLoginRedirect
  // when a signed-out visitor chooses "Sign in to accept" instead of creating a new account.)
  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  // A parent account only has the parent dashboard; every other account has no business on it. (This keeps the
  // navigation honest — the API enforces the same thing on its own.)
  const onParentArea = location.pathname === '/parent' || location.pathname.startsWith('/parent/');
  if (isParentUser(user) && !onParentArea) return <Navigate to="/parent" replace />;
  if (!isParentUser(user) && onParentArea) return <Navigate to="/dashboard" replace />;

  // If user is authenticated, allow access
  // (Database has the user, they passed login)
  // Trial/subscription checks can be done later if needed

  // Plan gating: a page whose feature isn't in the user's plan shows an upgrade page instead.
  const content = requiredFeature && !has(requiredFeature)
    ? <MainLayout><LockedFeature feature={requiredFeature} /></MainLayout>
    : children;

  // ...unless they still have to complete Getting Started (class/board/type), which every protected page requires first.
  return <OnboardingGuard userId={user?.id}>{content}</OnboardingGuard>;
};

export default ProtectedRoute;
